import axios, { AxiosRequestConfig, Method } from "axios"
import { catchError, map, Observable, of, Subject } from "rxjs"

import { AlertType } from "@/models/Alert"
import { HttpError } from "@/models/Error"
import { AlertActions, alertModule } from "@/store/modules/alert.module"

import { Endpoint } from "./endpoints"
import { errorHandlerService } from "./error-handler.service"
import { trackService } from "./track.service"

let connectionErrorShown = false
function resetConnectionErrorFlag() {
  setTimeout(() => {
    connectionErrorShown = false
  }, 5000)
}

function isNetworkError(e: Error | any): boolean {
  const errorMessages = [
    "timeout",
    "Network Error",
    "ERR_INTERNET_DISCONNECTED",
    "net::ERR",
    "ChunkLoadError",
    "Loading chunk"
  ]
  return (
    e.code === "ECONNABORTED" || (e?.message && errorMessages.some(msg => e.message.includes(msg)))
  )
}

interface Response<T> {
  response?: T
  error?: HttpError
}

export type RequestBody = { [key: string]: any } | boolean | number | string | undefined | null
export type RequestParams = { [key: string]: any } | undefined

class ApiService {
  async request<T>(
    method: Method,
    endpoint: Endpoint,
    body?: RequestBody,
    params?: RequestParams
  ): Promise<T | HttpError> {
    try {
      const { data } = await axios[method](endpoint.url, body, params)
      trackService.setPageLoadEventData(method, endpoint.logUrl, body)
      return data
    } catch (e: any) {
      if (axios.isCancel(e)) {
        const message = e.message || "Request canceled"
        return new Promise(resolve =>
          resolve(
            new HttpError({
              message,
              noErrorAlert: true
            })
          )
        )
      } else if (isNetworkError(e)) {
        if (!connectionErrorShown) {
          connectionErrorShown = true
          alertModule[AlertActions.SHOW_ALERT]({
            type: AlertType.ERROR,
            contentTitle: [{ text: "Connection timed out ", type: "bold" }],
            content: [{ text: "Please refresh", type: "regular" }],
            theme: "global",
            hideDetails: false
          })
          resetConnectionErrorFlag()
        }

        return new Promise(resolve =>
          resolve(
            new HttpError({
              message: "Connection timeout",
              noErrorAlert: true
            })
          )
        )
      } else if (endpoint.logsError) {
        errorHandlerService.onApiError(e)
      }
      return new Promise(resolve => resolve(new HttpError(e as HttpError)))
    }
  }

  async get<T>(endpoint: Endpoint, params?: RequestParams): Promise<T | HttpError> {
    return this.request("get", endpoint, { params })
  }
  async getBlob<T>(endpoint: Endpoint, params?: RequestParams): Promise<T | HttpError> {
    return this.request("get", endpoint, { params, responseType: "blob" })
  }
  async post<T>(
    endpoint: Endpoint,
    body?: RequestBody,
    params?: RequestParams
  ): Promise<T | HttpError> {
    return this.request("post", endpoint, body, this.getConfig(body, params))
  }
  async put<T>(
    endpoint: Endpoint,
    body?: RequestBody,
    params?: RequestParams
  ): Promise<T | HttpError> {
    return this.request("put", endpoint, body, { params })
  }
  async patch<T>(
    endpoint: Endpoint,
    body?: RequestBody,
    params?: RequestParams
  ): Promise<T | HttpError> {
    return this.request("patch", endpoint, body, this.getConfig(body, params))
  }
  async delete<T>(
    endpoint: Endpoint,
    body?: RequestBody,
    params?: RequestParams
  ): Promise<T | HttpError> {
    return this.request("delete", endpoint, { data: body, params: params })
  }

  request$<T>(
    method: Method,
    endpoint: Endpoint,
    body?: RequestBody,
    params?: RequestParams
  ): Observable<T> {
    const subject = new Subject<T>()
    try {
      axios[method](endpoint.url, body, params).then(({ data }) => {
        subject.next(data)
      })
    } catch (e) {
      errorHandlerService.onApiError(e)
      subject.error(e as HttpError)
    }
    return subject
  }

  get$<T>(endpoint: Endpoint, params?: RequestParams): Observable<Response<T>> {
    return this.request$<T>("get", endpoint, { params }).pipe(
      map((response: T) => ({ response })),
      catchError((error: HttpError) => of({ error }))
    )
  }
  getBlob$<T>(endpoint: Endpoint, params?: RequestParams): Promise<T | HttpError> {
    return this.request("get", endpoint, { params, responseType: "blob" })
  }
  post$<T>(endpoint: Endpoint, body?: RequestBody, params?: RequestParams): Promise<T | HttpError> {
    return this.request("post", endpoint, body, this.getConfig(body, params))
  }
  put$<T>(endpoint: Endpoint, body?: RequestBody, params?: RequestParams): Promise<T | HttpError> {
    return this.request("put", endpoint, body, { params })
  }
  patch$<T>(
    endpoint: Endpoint,
    body?: RequestBody,
    params?: RequestParams
  ): Promise<T | HttpError> {
    return this.request("patch", endpoint, body, { params })
  }
  delete$<T>(
    endpoint: Endpoint,
    body?: RequestBody,
    params?: RequestParams
  ): Promise<T | HttpError> {
    return this.request("delete", endpoint, { data: body, params: params })
  }

  getConfig(data: RequestBody, params?: RequestParams): AxiosRequestConfig {
    const config = { ...params } as AxiosRequestConfig
    if (data instanceof FormData) {
      config.headers = {
        "Content-Type": "multipart/form-data"
      }
    } else if (typeof data === "string") {
      config.headers = {
        "Content-Type": "text/plain",
        ...config.headers
      }
    } else if (typeof data === "boolean" || typeof data === "number") {
      config.headers = {
        "Content-Type": "application/json",
        ...config.headers
      }
    }
    return config
  }
}

export const apiService = new ApiService()
